import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { SERVICE_IMAGES } from "@/lib/images";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SectionOrnament from "@/components/SectionOrnament";
import { Check } from "lucide-react";

const SERVICES = [
  { key: "reinigungsservice", img: SERVICE_IMAGES.reinigungsservice },
  { key: "serviceTeam", img: SERVICE_IMAGES.serviceTeam },
  { key: "security", img: SERVICE_IMAGES.security },
];

export default function ServicesPage() {
  const { t } = useLanguage();
  const { hash } = useLocation();
  useSeo("Services", "Services", "Reinigungsservice, Service-Team und Security für euren Anlass.", "Service de nettoyage, équipe de service et sécurité pour votre événement.");

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero overline={t("services.overline")} title={t("services.title")} subtitle={t("services.sub")} />

      {SERVICES.map((s, i) => {
        const svc = t(`services.list.${s.key}`);
        const reversed = i % 2 === 1;
        return (
          <section
            key={s.key}
            id={s.key}
            data-testid={`service-${s.key}`}
            className={`relative py-20 sm:py-28 px-6 lg:px-10 scroll-mt-24 overflow-hidden ${i % 2 === 0 ? "bg-cream" : "bg-secondary/40"}`}
          >
            <SectionOrnament variant={i % 2 === 0 ? "corners" : "dots"} />
            <div className={`relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-center`}>
              <Reveal delay={80} className={reversed ? "lg:order-2" : ""}>
                <div className="zoom-parent aspect-[4/3] bg-champagne/30 border border-border">
                  <img src={s.img} alt={svc.name} loading="lazy" className="w-full h-full object-cover zoom-img" />
                </div>
              </Reveal>
              <Reveal className={reversed ? "lg:order-1" : ""}>
                <h2 className="font-heading text-3xl sm:text-4xl font-light mb-2">{svc.name}</h2>
                <p className="font-heading text-2xl text-gold mb-6">{svc.price}</p>
                <p className="text-ink-muted leading-relaxed mb-6 font-light">{svc.intro}</p>
                <p className="text-xs tracking-[0.2em] uppercase text-ink mb-4">{svc.featuresTitle}</p>
                <ul className="space-y-2.5 mb-6 text-sm text-ink/80">
                  {svc.features.map((f, j) => (
                    <li key={j} className="flex gap-3"><Check size={16} className="text-gold shrink-0 mt-1" /><span>{f}</span></li>
                  ))}
                </ul>
                <p className="text-sm text-ink-muted italic leading-relaxed mb-8">{svc.extra}</p>
                <Link
                  to={`/anfrage?service=${s.key}`}
                  data-testid={`service-cta-${s.key}`}
                  className="inline-block bg-ink text-cream px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-gold transition-colors duration-500"
                >
                  {t("services.cta")}
                </Link>
              </Reveal>
            </div>
          </section>
        );
      })}
    </div>
  );
}
